import React, { useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Icon } from "@iconify/react";
import "./EmailPreview.scss";

// Minimal allow-list HTML sanitizer (no external dependency).
//
// This used to also strip <style>, <meta>, <link>, <form>, etc. — but that
// meant any HTML email template with its design in a <style> block (i.e.
// almost all of them) rendered with zero styling, which was the root cause
// of "the html template doesn't show how it's rendered". Now that the
// content is rendered inside a sandboxed <iframe> (see below) rather than
// dangerouslySetInnerHTML'd into a live app <div>, the sandbox itself
// (no allow-scripts, no allow-forms, no allow-popups, no allow-top-navigation)
// is the real security boundary — scripts can't execute and forms can't
// submit regardless of what tags are present. So this function only needs
// to remove the handful of things a sandboxed iframe doesn't neutralize on
// its own: <script>/<iframe>/<object>/<embed> tags, inline on* handlers,
// and javascript:/data: URIs.
//
// If the project later adds DOMPurify or similar, swap this function's body
// for that call and leave the component API unchanged.
const sanitizeHtml = (html) => {
  if (!html) return "";

  let clean = String(html)
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<iframe[\s\S]*?<\/iframe>/gi, "")
    .replace(/<(object|embed)[\s\S]*?<\/\1>/gi, "")
    .replace(/<base\b[^>]*\/?>/gi, "");

  // Strip on* attributes (onclick, onerror, ...)
  clean = clean.replace(/\son\w+\s*=\s*(".*?"|'.*?'|[^\s>]+)/gi, "");

  // Neutralize script and executable data URIs in href/src.
  clean = clean.replace(
    /(href|src)\s*=\s*(["'])\s*(javascript:|vbscript:|data:(?!image\/(png|jpe?g|gif|webp)))[^"']*\2/gi,
    '$1="#"',
  );

  return clean;
};

// Callers don't always reliably set htmlBody=true - e.g. a body that came
// from pasting a full custom <!DOCTYPE html> document somewhere upstream
// that doesn't (or can't) flip that flag. Rather than trust the flag alone
// and silently fall back to dumping raw markup as literal text in a <p>,
// detect real HTML tags in the content itself as a backstop.
const HTML_TAG_REGEX = /<\/?[a-z][a-z0-9]*(\s[^<>]*)?\s*\/?>/i;
const looksLikeHtml = (value) => HTML_TAG_REGEX.test(value || "");

// Email templates are sometimes a full <html>...</html> document (with their
// own <head>/<style>), and sometimes just a body fragment. Either way we
// want the iframe to render a real document, with a small reset so bare
// fragments don't look cramped against the raw browser default margin.
const buildEmailDocument = (safeHtml) => {
  const isFullDocument = /<html[\s>]/i.test(safeHtml);
  if (isFullDocument) return safeHtml;

  return `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <style>
      html, body {
        margin: 0;
        padding: 16px;
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
          Helvetica, Arial, sans-serif;
        font-size: 14px;
        color: #1a1a1a;
      }
      img { max-width: 100%; }
      table { border-collapse: collapse; }
    </style>
  </head>
  <body>${safeHtml}</body>
</html>`;
};

/**
 * Renders a template body as either plain text or a real HTML email
 * document. Primarily driven by the `htmlBody` flag (matching the backend
 * contract where `{ body, htmlBody }` together indicate how `body` should
 * be interpreted), but also falls back to sniffing the content itself for
 * real markup - see `looksLikeHtml` - so a body that actually contains
 * `<html>...</html>` still renders as HTML even if the caller didn't (or
 * couldn't) set htmlBody=true.
 *
 * HTML bodies render inside a sandboxed <iframe srcDoc>, not
 * dangerouslySetInnerHTML into the page. That's what makes the preview
 * actually look like an email: the template's own <style>/<head> apply
 * inside the iframe's isolated document instead of being stripped or
 * bleeding into this app's CSS. The sandbox (allow-same-origin only — no
 * allow-scripts, allow-forms, allow-popups, or allow-top-navigation) is
 * what keeps that safe: nothing in the template can execute or navigate
 * anywhere, no matter what tags it contains.
 *
 * Compact by default (fixed height, internal scrolling). The eye button
 * opens an unclipped modal preview that grows to fit the full email.
 */
const EmailPreview = ({
  body,
  htmlBody,
  label = "Email Preview",
  variant = "compact",
}) => {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [modalFrameHeight, setModalFrameHeight] = useState(400);
  const modalFrameRef = useRef(null);

  const isHtml = Boolean(htmlBody) || looksLikeHtml(body);

  const srcDoc = useMemo(
    () => (isHtml ? buildEmailDocument(sanitizeHtml(body)) : null),
    [isHtml, body],
  );

  if (!body) return null;

  const openPreview = () => {
    setModalFrameHeight(400); // reset before the new frame loads/measures
    setIsPreviewOpen(true);
  };

  const handleModalFrameLoad = () => {
    const doc = modalFrameRef.current?.contentWindow?.document;
    const height = doc?.documentElement?.scrollHeight;
    if (height) {
      setModalFrameHeight(Math.min(Math.max(height + 24, 200), 2400));
    }
  };

  const templateContent = (className = "", { unclipped = false } = {}) => {
    if (!isHtml) {
      return <p className={`email-preview__text ${className}`}>{body}</p>;
    }

    return (
      <iframe
        title={label}
        srcDoc={srcDoc}
        // allow-same-origin only: no allow-scripts, allow-forms,
        // allow-popups, or allow-top-navigation. The template can't run
        // script, submit anywhere, or navigate the page — this is the
        // security boundary, not the regex above.
        sandbox="allow-same-origin"
        className={`email-preview__frame ${className}`}
        style={unclipped ? { height: modalFrameHeight } : undefined}
        ref={unclipped ? modalFrameRef : undefined}
        onLoad={unclipped ? handleModalFrameLoad : undefined}
      />
    );
  };

  return (
    <>
      <div className={`email-preview email-preview--${variant}`}>
        <div className="email-preview__header">
          <span className="email-preview__label">
            <Icon icon="mdi:email-outline" />
            {label}
          </span>
          <button
            type="button"
            className="email-preview__toggle"
            onClick={openPreview}
            aria-label="Open full email preview"
            title="Open full preview"
          >
            <Icon icon="mdi:eye-outline" />
          </button>
        </div>

        <div className="email-preview__body">{templateContent()}</div>
      </div>

      {isPreviewOpen &&
        createPortal(
          <div
            className="email-preview-modal"
            role="dialog"
            aria-modal="true"
            aria-label={`${label} full preview`}
            onMouseDown={() => setIsPreviewOpen(false)}
          >
            <div
              className="email-preview-modal__panel"
              onMouseDown={(event) => event.stopPropagation()}
            >
              <div className="email-preview-modal__header">
                <span>
                  <Icon icon="mdi:email-outline" /> {label}
                </span>
                <button
                  type="button"
                  onClick={() => setIsPreviewOpen(false)}
                  aria-label="Close full email preview"
                >
                  <Icon icon="mdi:close" />
                </button>
              </div>
              <div className="email-preview-modal__body">
                {templateContent("email-preview-modal__content", {
                  unclipped: true,
                })}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

export default EmailPreview;
